/**
 * Shared rendering for links we can describe better than the site will let us.
 *
 * The sync Lambda fetches OpenGraph metadata for bookmarks, but the sites that
 * matter most on a trip page are exactly the ones that refuse it: Airbnb 403s a
 * non-browser, and a Google Doc that is not public serves a sign-in page — whose
 * <title> is worse than no title at all. Both then fall back to a card titled
 * "docs.google.com/spreadsheets", and a link pasted inline stays a 200-character
 * URL.
 *
 * A provider reads what it can straight out of the URL; this module owns the DOM
 * work that is identical for all of them.
 */

export interface RichLink {
  /** Short brand name shown in the badge: "Airbnb", "Sheets". */
  brand: string;
  /** Variant class carrying the brand colour, e.g. "is-airbnb". */
  variant: string;
  /** Title to use when the link has no text a person wrote. */
  title: string;
  /** Small chips under the title: dates, guests, whatever the URL knew. */
  details?: string[];
  /**
   * Some sites hand back a title that is worse than nothing — a sign-in wall, or
   * their own product name. A provider can say which of those to throw away.
   */
  isJunkTitle?: (existing: string) => boolean;
}

export type LinkProvider = (url: URL, raw: string) => RichLink | null;

const providers: LinkProvider[] = [];

export function registerProvider(provider: LinkProvider): void {
  providers.push(provider);
}

function describe(raw: string): RichLink | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  for (const provider of providers) {
    const link = provider(url, raw);
    if (link) return link;
  }
  return null;
}

/**
 * True when a link's visible text is just its URL — or the hostname/path
 * stand-in the bookmark renderer falls back to. Anything a person actually
 * wrote is kept: it says more than we can infer from the URL.
 */
function looksLikeUrlText(text: string, href: string, url: URL, link: RichLink): boolean {
  const t = text.trim();
  if (!t) return true;
  if (t === href) return true;
  if (/^https?:\/\//i.test(t)) return true;
  if (link.isJunkTitle?.(t)) return true;
  // "airbnb.com/rooms", "docs.google.com/spreadsheets" — describeUrl()'s output.
  const host = url.hostname.replace(/^www\./, '').toLowerCase();
  return new RegExp(`^(www\\.)?${host.replace(/\./g, '\\.')}(/\\S*)?$`, 'i').test(t);
}

function badge(link: RichLink): HTMLElement {
  const el = document.createElement('span');
  el.className = 'link-badge';
  el.textContent = link.brand;
  return el;
}

/** The badge, then a chip per detail, on one wrapping row. */
function metaRow(link: RichLink): HTMLElement {
  const meta = document.createElement('span');
  meta.className = 'link-meta';
  meta.appendChild(badge(link));
  for (const d of link.details ?? []) {
    const chip = document.createElement('span');
    chip.className = 'link-chip';
    chip.textContent = d;
    meta.appendChild(chip);
  }
  return meta;
}

/**
 * Upgrades the server-rendered bookmark card. Its title is only replaced when
 * it is a fallback or a sign-in wall — a caption the trip owner wrote, or a real
 * OG title, still wins.
 */
function upgradeBookmarkCard(card: HTMLAnchorElement, url: URL, link: RichLink): void {
  card.classList.add('is-rich', link.variant);
  const href = card.getAttribute('href') ?? '';

  const titleEl = card.querySelector<HTMLElement>('.notion-bookmark-title');
  if (titleEl && looksLikeUrlText(titleEl.textContent ?? '', href, url, link)) {
    titleEl.textContent = link.title;
  }

  // A sign-in page's description is as useless as its title.
  const descEl = card.querySelector<HTMLElement>('.notion-bookmark-desc');
  if (descEl && link.isJunkTitle?.(descEl.textContent ?? '')) descEl.remove();

  // The host line said "docs.google.com" — the badge says it better, and brings
  // the dates and guest count with it.
  const meta = metaRow(link);
  const hostEl = card.querySelector<HTMLElement>('.notion-bookmark-host');
  if (hostEl) {
    hostEl.replaceWith(meta);
  } else {
    card.querySelector<HTMLElement>('.notion-bookmark-text')?.appendChild(meta);
  }
}

/** Replaces a bare pasted URL with a compact, readable pill. */
function upgradeInlineLink(a: HTMLAnchorElement, url: URL, link: RichLink): void {
  const href = a.getAttribute('href') ?? '';
  if (!looksLikeUrlText(a.textContent ?? '', href, url, link)) {
    // The author wrote real link text — it reads as prose, so it stays prose.
    // Only the badge is added, to say where the link goes.
    a.classList.add('link-tagged', link.variant);
    a.prepend(badge(link));
    return;
  }

  a.classList.add('link-pill', link.variant);
  const label = document.createElement('span');
  label.className = 'link-pill-label';
  label.textContent = [link.title, ...(link.details ?? [])].join(' · ');
  a.textContent = '';
  a.appendChild(badge(link));
  a.appendChild(label);
}

/**
 * Rewrites every link a provider recognises. Runs after sanitizing, and only
 * ever builds nodes itself, so it reintroduces no untrusted HTML.
 */
export function enhanceRichLinks(container: HTMLElement): void {
  container.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    const link = describe(href);
    if (!link) return;
    const url = new URL(href);
    if (a.classList.contains('notion-bookmark')) {
      upgradeBookmarkCard(a, url, link);
    } else if (!a.closest('.notion-bookmark')) {
      upgradeInlineLink(a, url, link);
    }
  });
}
